import { useRef } from 'react';
import { motion } from 'framer-motion';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  ReferenceLine,
} from 'recharts';
import { GlassCard } from '../ui/GlassCard';
import { Activity } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { LatencyPoint, AgentLatency } from '../../hooks/useMetricsData';

// ─── Custom Tooltip ──────────────────────────────────────────────────

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    dataKey: string;
    color: string;
    payload: { name: string };
  }>;
  label?: string;
}

function LatencyTooltip({ active, payload }: TooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass-panel px-3 py-2 rounded-xl border border-white/10 shadow-xl">
      <div className="space-y-1">
        {payload.map((p) => (
          <div key={p.dataKey} className="flex items-center gap-2">
            <span
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: p.color }}
            />
            <span className="text-xs text-text-muted capitalize">{p.dataKey}:</span>
            <span className="text-xs font-semibold text-text-primary tabular-nums">
              {Math.round(p.value)}ms
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BarTooltip({ active, payload }: TooltipProps) {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <div className="glass-panel px-3 py-2 rounded-xl border border-white/10 shadow-xl">
      <div className="flex items-center gap-2">
        <span
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: p.color }}
        />
        <span className="text-xs text-text-muted">{p.payload.name}:</span>
        <span className="text-xs font-semibold text-text-primary tabular-nums">
          {Math.round(p.value)}ms
        </span>
      </div>
    </div>
  );
}

// ─── STT Latency Chart ───────────────────────────────────────────────

function STTLatencyChart({ data }: { data: LatencyPoint[] }) {
  return (
    <GlassCard
      variant="default"
      header={
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-vokentra" />
          <span className="text-sm font-semibold text-text-primary">
            Speech-to-Text Latency
          </span>
        </div>
      }
    >
      <div className="h-[220px] -mx-2">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="sttP50" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00bfff" stopOpacity={0.25} />
                <stop offset="100%" stopColor="#00bfff" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="sttP95" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00d084" stopOpacity={0.15} />
                <stop offset="100%" stopColor="#00d084" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="sttP99" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ffa500" stopOpacity={0.1} />
                <stop offset="100%" stopColor="#ffa500" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(255,255,255,0.04)"
              vertical={false}
            />
            <XAxis
              dataKey="timestamp"
              tickFormatter={(v) => {
                const d = new Date(v);
                return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}:${d.getSeconds().toString().padStart(2, '0')}`;
              }}
              tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              interval={4}
            />
            <YAxis
              tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              domain={[0, 500]}
              tickFormatter={(v) => `${v}`}
            />
            <Tooltip content={<LatencyTooltip />} />
            <ReferenceLine
              y={300}
              stroke="#e60000"
              strokeDasharray="6 3"
              strokeWidth={1}
              label={{
                value: 'Target: 300ms',
                position: 'insideTopRight',
                fill: '#e60000',
                fontSize: 10,
              }}
            />
            <Area
              type="monotone"
              dataKey="p50"
              stroke="#00bfff"
              strokeWidth={2}
              fill="url(#sttP50)"
              dot={false}
              isAnimationActive={false}
            />
            <Area
              type="monotone"
              dataKey="p95"
              stroke="#00d084"
              strokeWidth={1.5}
              fill="url(#sttP95)"
              dot={false}
              isAnimationActive={false}
            />
            <Area
              type="monotone"
              dataKey="p99"
              stroke="#ffa500"
              strokeWidth={1.5}
              fill="url(#sttP99)"
              dot={false}
              strokeDasharray="4 2"
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {/* Legend */}
      <div className="flex items-center gap-4 mt-2 justify-center">
        {[
          { key: 'p50', color: '#00bfff', label: 'p50' },
          { key: 'p95', color: '#00d084', label: 'p95' },
          { key: 'p99', color: '#ffa500', label: 'p99' },
        ].map((item) => (
          <div key={item.key} className="flex items-center gap-1.5">
            <span
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: item.color }}
            />
            <span className="text-[10px] text-text-muted">{item.label}</span>
          </div>
        ))}
      </div>
    </GlassCard>
  );
}

// ─── Agent Execution Latency ─────────────────────────────────────────

function AgentExecutionLatency({ data }: { data: AgentLatency[] }) {
  const barColor = (latency: number): string => {
    if (latency < 350) return '#00d084';
    if (latency < 500) return '#00bfff';
    if (latency < 650) return '#ffa500';
    return '#e60000';
  };

  return (
    <GlassCard
      variant="default"
      header={
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-success" />
          <span className="text-sm font-semibold text-text-primary">
            Agent Response Time
          </span>
        </div>
      }
    >
      <div className="h-[220px] -mx-2">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(255,255,255,0.04)"
              vertical={false}
            />
            <XAxis
              dataKey="name"
              tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v: string) => v.replace('Agent', '')}
            />
            <YAxis
              tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              domain={[0, 900]}
              tickFormatter={(v) => `${v}`}
            />
            <Tooltip content={<BarTooltip />} />
            <Bar
              dataKey="avgLatency"
              radius={[6, 6, 0, 0]}
              maxBarSize={40}
              isAnimationActive={true}
              animationDuration={800}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={barColor(entry.avgLatency)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      {/* Legend */}
      <div className="flex items-center gap-3 mt-2 justify-center">
        {[
          { color: '#00d084', label: '< 350ms' },
          { color: '#00bfff', label: '350-500ms' },
          { color: '#ffa500', label: '500-650ms' },
          { color: '#e60000', label: '> 650ms' },
        ].map((item) => (
          <div key={item.label} className="flex items-center gap-1.5">
            <span
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: item.color }}
            />
            <span className="text-[10px] text-text-muted">{item.label}</span>
          </div>
        ))}
      </div>
    </GlassCard>
  );
}

// ─── TTS Latency Chart ───────────────────────────────────────────────

function TTSLatencyChart({ data }: { data: LatencyPoint[] }) {
  return (
    <GlassCard
      variant="default"
      header={
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-info" />
          <span className="text-sm font-semibold text-text-primary">
            Text-to-Speech Latency
          </span>
        </div>
      }
    >
      <div className="h-[220px] -mx-2">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="ttsP50" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00bfff" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#00bfff" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="ttsP95" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00d084" stopOpacity={0.15} />
                <stop offset="100%" stopColor="#00d084" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(255,255,255,0.04)"
              vertical={false}
            />
            <XAxis
              dataKey="timestamp"
              tickFormatter={(v) => {
                const d = new Date(v);
                return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}:${d.getSeconds().toString().padStart(2, '0')}`;
              }}
              tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              interval={4}
            />
            <YAxis
              tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              domain={[0, 450]}
              tickFormatter={(v) => `${v}`}
            />
            <Tooltip content={<LatencyTooltip />} />
            <ReferenceLine
              y={200}
              stroke="#00d084"
              strokeDasharray="6 3"
              strokeWidth={1}
              label={{
                value: 'Target: 200ms',
                position: 'insideTopRight',
                fill: '#00d084',
                fontSize: 10,
              }}
            />
            <Area
              type="monotone"
              dataKey="p50"
              stroke="#00bfff"
              strokeWidth={2}
              fill="url(#ttsP50)"
              dot={false}
              isAnimationActive={false}
            />
            <Area
              type="monotone"
              dataKey="p95"
              stroke="#00d084"
              strokeWidth={1.5}
              fill="url(#ttsP95)"
              dot={false}
              strokeDasharray="4 2"
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {/* Legend */}
      <div className="flex items-center gap-4 mt-2 justify-center">
        {[
          { key: 'p50', color: '#00bfff', label: 'Time-to-first-byte (p50)' },
          { key: 'p95', color: '#00d084', label: 'p95' },
        ].map((item) => (
          <div key={item.key} className="flex items-center gap-1.5">
            <span
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: item.color }}
            />
            <span className="text-[10px] text-text-muted">{item.label}</span>
          </div>
        ))}
      </div>
    </GlassCard>
  );
}

// ─── Exported Component ──────────────────────────────────────────────

interface LatencyDashboardProps {
  sttLatency: LatencyPoint[];
  agentLatency: AgentLatency[];
  ttsLatency: LatencyPoint[];
  className?: string;
}

export function LatencyDashboard({
  sttLatency,
  agentLatency,
  ttsLatency,
  className,
}: LatencyDashboardProps) {
  return (
    <div className={cn('grid grid-cols-1 lg:grid-cols-3 gap-4', className)}>
      <STTLatencyChart data={sttLatency} />
      <AgentExecutionLatency data={agentLatency} />
      <TTSLatencyChart data={ttsLatency} />
    </div>
  );
}

export default LatencyDashboard;
